import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getCustomFieldMappings } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFieldMappings";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useCallback, useMemo } from "react";
import { IssueCustomAttributeMapping, IssueType } from "@adsk/acc-issues-types";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";

/**
 * Returns a memoized function that gets an issue type ID and returns an
 * ordered list of all custom fields for that issue type, including custom
 * fields mapped for the entire project and custom fields mapped for the
 * category.
 */
export const useGetCustomFieldMappingsByIssueTypeId = () => {
  const customFieldMappings = useSelector(getCustomFieldMappings);
  const projectId = useCurrentProjectId()!;
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  return useCallback(
    (issueTypeId): IssueCustomAttributeMapping[] => {
      const attributesFromProjectMapping = sortBy(
        customFieldMappings[projectId] || [],
        "order"
      );
      const issueType = issueTypeEntitiesMap[issueTypeId];
      const categoryId =
        issueType && (issueType as IssueType).issueTypeCategoryId;
      const attributesFromCategoryMapping = categoryId
        ? sortBy(customFieldMappings[categoryId] || [], "order")
        : [];
      const attributesFromTypeMapping = issueTypeId
        ? sortBy(customFieldMappings[issueTypeId] || [], "order")
        : [];
      return uniqBy(
        [
          ...attributesFromProjectMapping,
          ...attributesFromCategoryMapping,
          ...attributesFromTypeMapping,
        ],
        "id"
      );
    },
    [customFieldMappings, projectId, issueTypeEntitiesMap]
  );
};

export const useMemoizedIssueCustomFieldMappingsByTypeId = (
  issueTypeId?: string
) => {
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();
  return useMemo(
    () => getCustomFieldMappingsByIssueTypeId(issueTypeId),
    [issueTypeId, getCustomFieldMappingsByIssueTypeId]
  );
};

export const useCustomFieldIdsWithMappingsForType = (
  issueTypeId?: string
): {
  customFieldIds: string[];
  mappingsByCustomFieldId: Record<string, IssueCustomAttributeMapping>;
} => {
  const issueCustomFieldMappings =
    useMemoizedIssueCustomFieldMappingsByTypeId(issueTypeId);
  const mappingsByCustomFieldId: Record<string, IssueCustomAttributeMapping> =
    {};
  const customFieldIds = issueCustomFieldMappings.map(
    (mapping: IssueCustomAttributeMapping) => {
      // build the mappingsByCustomFieldId object while returning the ordered list of custom field ids
      mappingsByCustomFieldId[mapping.attributeDefinitionId] = mapping;
      return mapping.attributeDefinitionId;
    }
  );
  return {
    customFieldIds,
    mappingsByCustomFieldId,
  };
};
